import { z } from 'zod'
import type { ApiClient } from './client'
import { ackEnvelope, assertAck } from './envelope'

/**
 * Workbooks — POST /{clusterId}/api/workbook/list
 *
 * The legacy console's detection library. Every workbook is a saved,
 * executable query notebook; the ones that run scheduled/streamed are the
 * tenant's detections, and their signals carry the workbook id as $SourceId.
 *
 * ⚠️ Shape is UNVERIFIED — reconstructed from the legacy list screen
 * (saas-console/newyork src/pages/workbook/workbookList/index.js column
 * dataIndexes) on 2026-08-22. No live capture exists for this endpoint yet
 * (docs/inventory/api-observed.json has no /workbook route sweep). Before
 * live mode ships, capture like docs/inventory/attack-capture.md and tighten
 * this reader. Until then: hand-rolled tolerant reader, same rationale as
 * attack.ts — the list can run to thousands of rows and any field may go
 * null.
 *
 * Update 2026-09-24/29: the paged list and versionview ARE captured now
 * (docs/inventory/detections-capture.md, workbook-blocks-capture.md), and
 * listPage / listOverview / versionDoc parse through the zod schemas under
 * "wire schemas" below, with the readers as the mapping layer. `list` and
 * `detail` still read raw.
 *
 * Fields the legacy list renders, and what they mean:
 *   type           'standard' | 'scheduled' | 'auto-scheduled'
 *                  (UI words: Off / Scheduled / Streamed) — the run mode
 *   stage          'dev' | 'beta' | 'prod' (+ 'test' seen in filter code)
 *   author         'DNIF' | 'community' | anything else = custom author
 *   disabled       enabled state (workbook/visibility flips it)
 *   is_locked      content-manager lock; locked rows refuse edits downstream
 *   is_private     private workbooks hide row actions in legacy
 *   signal_count   signals in the last 30d
 *   signal_last_seen  ms epoch of the last signal (null = none)
 *   signal_tags    classification tags
 *   stream_flows   streams the workbook reads (arrives async in legacy —
 *                  may be absent from the list response itself)
 *   signal_kb      per-detection knowledge-base markdown
 *   last_modified_tstamp  EPOCH SECONDS (legacy multiplies by 1000)
 */

export interface Workbook {
  id: string
  name: string
  /** Folder path, '/' separated. */
  path: string
  author: string
  /** 'standard' | 'scheduled' | 'auto-scheduled'. */
  type: string
  stage: string
  version: string
  disabled: boolean
  is_locked: boolean
  is_private: boolean
  signal_count: number
  /** ms epoch; null when the workbook never fired. */
  signal_last_seen: number | null
  signal_tags: string[]
  stream_flows: string[]
  signal_kb: string
  /** Epoch seconds. */
  last_modified_tstamp: number | null
  create_tstamp: number | null
  last_updated_tstamp: number | null
}

export function readWorkbook(v: unknown): Workbook | null {
  if (typeof v !== 'object' || v === null) return null
  const o = v as Record<string, unknown>
  const str = (x: unknown) => (typeof x === 'string' ? x : '')
  const bool = (x: unknown) => x === true
  const num = (x: unknown) => (typeof x === 'number' && Number.isFinite(x) ? x : 0)
  const numOrNull = (x: unknown) =>
    typeof x === 'number' && Number.isFinite(x) && x > 0 ? x : null
  const strings = (x: unknown) =>
    Array.isArray(x) ? x.filter((s): s is string => typeof s === 'string' && s !== '') : []
  const id = typeof o.id === 'string' ? o.id : typeof o.id === 'number' ? String(o.id) : ''
  const name = str(o.name)
  if (!id || !name) return null
  return {
    id,
    name,
    path: str(o.path),
    author: str(o.author),
    type: str(o.type),
    stage: str(o.stage),
    version: str(o.version) || (typeof o.version === 'number' ? String(o.version) : ''),
    disabled: bool(o.disabled),
    is_locked: bool(o.is_locked),
    is_private: bool(o.is_private),
    signal_count: num(o.signal_count),
    signal_last_seen: numOrNull(o.signal_last_seen),
    signal_tags: strings(o.signal_tags),
    stream_flows: strings(o.stream_flows),
    signal_kb: str(o.signal_kb),
    last_modified_tstamp: numOrNull(o.last_modified_tstamp),
    create_tstamp: numOrNull(o.create_tstamp),
    last_updated_tstamp: numOrNull(o.last_updated_tstamp),
  }
}

export function readWorkbooks(json: unknown): Workbook[] {
  if (typeof json !== 'object' || json === null) return []
  const body = (json as Record<string, unknown>).data
  if (!Array.isArray(body)) return []
  return body.map(readWorkbook).filter((w): w is Workbook => w !== null)
}

/* ------------------------------------------------------------ paged list */

/**
 * The MITRE/severity a workbook's signal block declares — what the paged
 * library renders per row. Captured 2026-09-24
 * (docs/inventory/detections-capture.json): values arrive as display
 * strings ("Medium", "Reconnaissance", "Active Scanning",
 * "T1595 - Active Scanning"), score as a string.
 */
export interface WorkbookSignalMeta {
  severity: string
  score: string
  tactic: string
  technique: string
  subtechnique: string
}

/** One row of a paged workbook/list — the library row plus what the
 * capture showed the list itself carries. */
export interface WorkbookRow extends Workbook {
  /** Streams the workbook reads (the captured row's `stream`). */
  stream: string[]
  /** One entry per signal block, in block order. */
  signals: WorkbookSignalMeta[]
  /** `signal_count` as delivered — null when the backend sent none (every
   * captured row), which `signal_count` itself reads as 0. */
  signalCountRaw: number | null
}

export interface WorkbookPage {
  rows: WorkbookRow[]
  /** The envelope's `count` — every row matching the filters, not this page. */
  total: number
}

export function readWorkbookRow(v: unknown): WorkbookRow | null {
  const base = readWorkbook(v)
  if (!base) return null
  const o = v as Record<string, unknown>
  const str = (x: unknown) => (typeof x === 'string' ? x : typeof x === 'number' ? String(x) : '')
  const stream = Array.isArray(o.stream)
    ? o.stream.filter((s): s is string => typeof s === 'string' && s !== '')
    : []
  const signals = (Array.isArray(o.blocks) ? o.blocks : [])
    .filter(
      (b): b is Record<string, unknown> =>
        typeof b === 'object' && b !== null && (b as Record<string, unknown>).type === 'signal',
    )
    .map((b) => ({
      severity: str(b.DetectionSeverity),
      score: str(b.DetectionScore),
      tactic: str(b.DetectionTactic),
      technique: str(b.DetectionTechnique),
      subtechnique: str(b.DetectionSubtechnique),
    }))
  const signalCountRaw =
    typeof o.signal_count === 'number' && Number.isFinite(o.signal_count) ? o.signal_count : null
  return { ...base, stream, signals, signalCountRaw }
}

export function readWorkbookPage(json: unknown): WorkbookPage {
  if (typeof json !== 'object' || json === null) return { rows: [], total: 0 }
  const o = json as Record<string, unknown>
  if (!Array.isArray(o.data)) return { rows: [], total: 0 }
  const rows = o.data.map(readWorkbookRow).filter((r): r is WorkbookRow => r !== null)
  const total = typeof o.count === 'number' && Number.isFinite(o.count) ? o.count : rows.length
  return { rows, total }
}

/* ------------------------------------------------------- wire schemas */

/*
 * zod schemas for the workbook/list and workbook/versionview responses
 * (constitution rule 5), from the 2026-09-24 and 2026-09-28/29 captures
 * (docs/inventory/detections-capture.json, workbook-blocks-capture.md).
 *
 * What is STRICT: the envelope — `data`, when present, must be an ARRAY of
 * row objects (a `{ result: [...] }` or `{ object_id }` payload is a shape
 * mismatch, not an empty library), `count` a number, and each row an object.
 * What is PERMISSIVE: every row field. They are optional and nullable, ids
 * and versions take a string OR a number, and unknown keys pass through.
 *
 * Deliberately no `.default()` / `.transform()` on a row: the doc reader keeps
 * the row as `raw` and the editor saves from it, so the parse must hand back
 * the row exactly as delivered — an added `blocks: []` or a stringified
 * `version` would be written back on the next save. The readers below stay
 * the mapping layer (string/number → string, null → '' / 0, junk rows dropped).
 */

const idLike = z.union([z.string(), z.number()])
const optStr = z.string().nullable().optional()
const optNum = z.number().nullable().optional()
/** `0/1` is accepted beside booleans; the reader treats only `true` as set. */
const optFlag = z.union([z.boolean(), z.number()]).nullable().optional()
const optStrings = z.array(z.string().nullable()).nullable().optional()
/** Signal-block display fields — strings live ("5", "Medium"); a number reads the same. */
const optText = z.union([z.string(), z.number()]).nullable().optional()

/** One notebook block. Only `type` is read structurally; payloads vary per type and pass through. */
export const workbookBlockWire = z
  .object({
    type: z.string().optional(),
    DetectionSeverity: optText,
    DetectionScore: optText,
    DetectionTactic: optText,
    DetectionTechnique: optText,
    DetectionSubtechnique: optText,
  })
  .passthrough()

/** One workbook row as workbook/list (and versionview) deliver it. */
export const workbookRowWire = z
  .object({
    id: idLike.optional(),
    name: optStr,
    path: optStr,
    author: optStr,
    type: optStr,
    stage: optStr,
    description: optStr,
    version: idLike.nullable().optional(),
    disabled: optFlag,
    is_locked: optFlag,
    is_private: optFlag,
    /** null on every captured row; a number when the backend fills it. */
    signal_count: optNum,
    signal_last_seen: optNum,
    signal_tags: optStrings,
    stream: optStrings,
    stream_flows: optStrings,
    signal_kb: optStr,
    last_modified_tstamp: optNum,
    create_tstamp: optNum,
    last_updated_tstamp: optNum,
    blocks: z.array(workbookBlockWire).nullable().optional(),
    config: z
      .object({ parameters: z.array(z.object({}).passthrough()).nullable().optional() })
      .passthrough()
      .nullable()
      .optional(),
  })
  .passthrough()

/**
 * `POST /{c}/api/workbook/list` — `{TStamp, count, data: Workbook[], message,
 * status}`. `count` is the total matching the filters, not this page.
 */
export const workbookListResponse = z
  .object({
    status: z.string().optional(),
    message: z.string().optional(),
    TStamp: z.string().optional(),
    count: z.number().nullable().optional(),
    data: z.array(workbookRowWire).nullable().optional(),
  })
  .passthrough()

/**
 * `POST /{c}/api/workbook/versionview` — the list envelope with the version's
 * document in `data` ("Workbook version displayed", 2026-09-29). Only the
 * message was captured, not the body, so a bare document in `data` is read
 * too rather than refused.
 */
export const workbookVersionViewResponse = z
  .object({
    status: z.string().optional(),
    message: z.string().optional(),
    TStamp: z.string().optional(),
    data: z.union([z.array(workbookRowWire), workbookRowWire]).nullable().optional(),
  })
  .passthrough()

/**
 * One server-side filter. Keys observed working 2026-09-24: direct row
 * fields (`path` = folder id, `stream`, `disabled`, `stage`, `tags`,
 * `author`, `type`) and signal-block fields (`DetectionTactic`,
 * `DetectionTechnique`, `DetectionSeverity`, `DetectionScore`).
 */
export interface WorkbookListFilter {
  key: string
  /** LIKE: case-insensitive substring — confirmed on `name` (workbook-blocks-capture.md). */
  operand: '=' | 'IN' | 'LIKE'
  value: string | boolean | string[]
}

export function workbookFilterWire(f: WorkbookListFilter): Record<string, unknown> {
  return {
    filter_key: f.key,
    filter_condition: 'AND',
    filter_operand: f.operand,
    filter_value: f.value,
  }
}

/* -------------------------------------------------------------- full doc */

/**
 * One notebook block. Legacy types observed in the editor's load loop:
 * dql (gui when `block` is truthy), sql, code (base64 in `code`), text
 * (plain in `code`), call/return (`query` + `path` + `name`), signal, ai,
 * visual, notification, simulation. Unknown types must render as opaque
 * rather than drop — the doc is user content.
 */
export interface WorkbookBlock {
  type: string
  query: string
  /** code blocks: base64 source; text blocks: the text itself. */
  code: string
  name: string
  path: string
  /** dql only: truthy = authored in the GUI builder. */
  gui: boolean
  /**
   * The block EXACTLY as the wire delivered it. Saving re-serializes from
   * here for any type this client cannot author (signal, ai, visual,
   * notification, …) — the typed fields above are a lossy read for
   * display, and a save built from them would destroy those payloads.
   */
  raw: Record<string, unknown>
}

/**
 * One workbook parameter. Stored as `config.parameters[]`, captured
 * 2026-09-29 as `{fixed, name, query, queryFieldValue, type, value}`; the
 * console edits name and value (the "Default Value") only, and `raw` keeps
 * every other key as stored so a save does not drop them.
 */
export interface WorkbookParameter {
  name: string
  value: string
  /** The parameter as delivered (absent on one built by hand). */
  raw?: Record<string, unknown>
}

/** A parameter added in the console — the legacy editor's new-row shape. */
export function newWorkbookParameterRaw(): Record<string, unknown> {
  return { fixed: false, name: '', query: '', queryFieldValue: [], type: 'Text', value: '' }
}

/** A parameter back to its stored shape: the raw keys, name and value on top. */
export function serializeWorkbookParameter(p: WorkbookParameter): Record<string, unknown> {
  return { ...(p.raw ?? {}), name: p.name, value: p.value }
}

/** The full workbook document — a library row plus its notebook content. */
export interface WorkbookDoc extends Workbook {
  description: string
  blocks: WorkbookBlock[]
  parameters: WorkbookParameter[]
  /** The document as delivered — save serializes from here so fields this
   * client does not model (properties, tag_list, cron_obj, …) survive. */
  raw: Record<string, unknown>
}

export function readWorkbookBlock(v: unknown): WorkbookBlock | null {
  if (typeof v !== 'object' || v === null) return null
  const o = v as Record<string, unknown>
  const str = (x: unknown) => (typeof x === 'string' ? x : '')
  if (typeof o.type !== 'string' || !o.type) return null
  return {
    type: o.type,
    query: str(o.query),
    code: str(o.code),
    name: str(o.name),
    path: str(o.path),
    gui: Boolean(o.block),
    raw: { ...o },
  }
}

/**
 * A block back to its wire shape. Types this client can author (dql, sql,
 * text, code) are rebuilt from the typed fields ON TOP of the raw object,
 * so wire-only extras (hasSourceName, the gui `block` flag, code language/
 * encoding) survive an edit; every other type ships its raw form verbatim.
 * A block created in this client has an empty raw — the defaults below
 * mirror what the legacy editor writes on save.
 */
export function serializeWorkbookBlock(b: WorkbookBlock): Record<string, unknown> {
  switch (b.type) {
    case 'dql':
      return { ...b.raw, type: 'dql', query: b.query, ...(b.gui ? { block: b.raw.block ?? true } : {}) }
    case 'sql':
      return { ...b.raw, type: 'sql', query: b.query }
    case 'text':
      return { ...b.raw, type: 'text', code: b.code }
    case 'code':
      return {
        language: 'python-3.8',
        encoding: 'bytecode',
        ...b.raw,
        type: 'code',
        code: b.code, // already base64 — the editor encodes before setting
      }
    default:
      return Object.keys(b.raw).length ? b.raw : { type: b.type, query: b.query, code: b.code }
  }
}

export function readWorkbookDoc(v: unknown): WorkbookDoc | null {
  const base = readWorkbook(v)
  if (!base) return null
  const o = v as Record<string, unknown>
  const blocks = Array.isArray(o.blocks)
    ? o.blocks.map(readWorkbookBlock).filter((b): b is WorkbookBlock => b !== null)
    : []
  const config =
    typeof o.config === 'object' && o.config !== null ? (o.config as Record<string, unknown>) : {}
  const params = Array.isArray(config.parameters) ? config.parameters : []
  const parameters = params
    .map((p): WorkbookParameter | null => {
      if (typeof p !== 'object' || p === null) return null
      const po = p as Record<string, unknown>
      return typeof po.name === 'string'
        ? {
            name: po.name,
            value: typeof po.value === 'string' ? po.value : String(po.value ?? ''),
            raw: { ...po },
          }
        : null
    })
    .filter((p): p is WorkbookParameter => p !== null)
  return {
    ...base,
    description: typeof o.description === 'string' ? o.description : '',
    blocks,
    parameters,
    raw: { ...o },
  }
}

/**
 * The save envelope's wbk_data — the exact field set the legacy editor
 * writes (its `wbData.wbk_data`), seeded from the document's raw form so
 * unmodeled fields (properties, tag_list, cron/cron_obj, timezones, type)
 * ride through, then overridden with what this client edits.
 */
export function serializeWorkbookSave(
  doc: WorkbookDoc,
  blocks: WorkbookBlock[],
  commitMsg: string,
): Record<string, unknown> {
  const r = doc.raw
  const type = r.type ?? doc.type ?? 'standard'
  return {
    id: doc.id,
    commit_msg: commitMsg || (typeof r.commit_msg === 'string' ? r.commit_msg : ''),
    stage: r.stage ?? doc.stage,
    profile: r.profile ?? null,
    config: r.config ?? { parameters: doc.parameters.map(serializeWorkbookParameter) },
    name: doc.name,
    path: r.path ?? doc.path,
    active: 1,
    description: doc.description,
    properties: r.properties ?? {},
    fields: r.fields ?? [],
    blocks: blocks.map(serializeWorkbookBlock),
    // Only when the document has one — an invented [] could clear tags.
    ...(r.tag_list !== undefined ? { tag_list: r.tag_list } : {}),
    type,
    /*
     * Schedule fields travel with the TYPE, on every save (verified live
     * 2026-09-29): a Scheduled workbook saved WITHOUT cron / cron_obj, and a
     * standard one saved WITH them (or with schedule_timezone), are both refused
     * "Unable to schedule a workbook". Off / Streamed: none of them; query_timezone
     * never (the legacy edit does not send it).
     */
    ...(type === 'scheduled'
      ? { cron: r.cron ?? null, cron_obj: r.cron_obj ?? {}, schedule_timezone: r.schedule_timezone ?? 'UTC' }
      : {}),
  }
}

/* ---------------------------------------------------------------- versions */

/**
 * One row of the version history (workbook/versionlist). Captured
 * 2026-09-29: `{commit_msg, id, kb_file_url, last_modified_tstamp, version,
 * version_author}` with `version` a NUMBER (demo data uses 'v4'-style
 * strings); read as a string either way.
 */
export interface WorkbookVersion {
  version: string
  commit_msg: string
  version_author: string
  /** Epoch seconds. */
  last_modified_tstamp: number | null
}

export function readWorkbookVersion(v: unknown): WorkbookVersion | null {
  if (typeof v !== 'object' || v === null) return null
  const o = v as Record<string, unknown>
  const version =
    typeof o.version === 'string' ? o.version : typeof o.version === 'number' ? String(o.version) : ''
  if (!version) return null
  return {
    version,
    commit_msg: typeof o.commit_msg === 'string' ? o.commit_msg : '',
    version_author: typeof o.version_author === 'string' ? o.version_author : '',
    last_modified_tstamp:
      typeof o.last_modified_tstamp === 'number' && o.last_modified_tstamp > 0
        ? o.last_modified_tstamp
        : null,
  }
}

/**
 * A version as the version endpoints take it: the number the versionlist
 * delivered when the string is numeric ('5' → 5), otherwise as given.
 */
export function versionWire(version: string): string | number {
  return /^\d+$/.test(version) ? Number(version) : version
}

/* -------------------------------------------------------------------- meta */

/** The option lists `GET /api/workbook/meta?type=` serves. */
export type WorkbookMetaType = 'stage' | 'profile'

/**
 * `GET /{clusterId}/api/workbook/meta?type=stage|profile`, captured
 * 2026-09-29: `{TStamp, count, data: ["prod","beta","dev","test"], message,
 * status}` (profile: `["Basic","Essential","Custom"]`). No scope_id.
 */
export const workbookMetaResponse = z
  .object({
    status: z.string().optional(),
    message: z.string().optional(),
    count: z.number().optional(),
    data: z.array(z.string().nullable()).default([]),
  })
  .passthrough()

/**
 * POST /api/workbook/checklockedworkbook {scope_id, wbid} — the check the
 * console runs before opening a signal's source workbook. CAPTURED 2026-10-05
 * (docs/inventory/signals-queue-capture.md):
 *   - `wbid` is the signal's `$SourceId` (32-hex). `$Workbook` is the NAME and
 *     answers `{status: 'success', message: 'Workbook does not exist!', data: {}}`.
 *   - an unlocked workbook answers `{status: 'success', message: '', data: {is_private: false}}`.
 * A locked / private workbook's reply was NOT observed; `is_private: true` is
 * read as the refusal until a capture says otherwise.
 */
export const workbookLockResponse = z
  .object({
    status: z.string().optional(),
    message: z.string().optional(),
    data: z
      .union([z.object({ is_private: z.boolean().optional() }).passthrough(), z.array(z.unknown()), z.null()])
      .optional(),
  })
  .passthrough()

export interface WorkbookLockCheck {
  status: string
  message: string
  /** null when the reply carried no `is_private` (e.g. the workbook does not exist). */
  isPrivate: boolean | null
}

export function readWorkbookLock(res: z.output<typeof workbookLockResponse>): WorkbookLockCheck {
  const data = res.data && !Array.isArray(res.data) ? res.data : null
  return {
    status: res.status ?? '',
    message: (res.message ?? '').trim(),
    isPrivate: typeof data?.is_private === 'boolean' ? data.is_private : null,
  }
}

export function readWorkbookMeta(res: z.output<typeof workbookMetaResponse>): string[] {
  return res.data.filter((v): v is string => typeof v === 'string' && v.trim() !== '')
}

/* ----------------------------------------------------------------- helpers */

export type WorkbookOrigin = 'native' | 'community' | 'custom'

/** DNIF-authored ships as native content; 'community' is its own channel. */
export function workbookOrigin(w: Pick<Workbook, 'author'>): WorkbookOrigin {
  const a = w.author.trim().toLowerCase()
  if (a === 'dnif') return 'native'
  if (a === 'community') return 'community'
  return 'custom'
}

export type WorkbookRunMode = 'manual' | 'scheduled' | 'streamed'

/** Legacy vocabulary: standard=Off/manual, auto-scheduled=Streamed. */
export function workbookRunMode(w: Pick<Workbook, 'type'>): WorkbookRunMode {
  if (w.type === 'auto-scheduled') return 'streamed'
  if (w.type === 'scheduled') return 'scheduled'
  return 'manual'
}

/**
 * Native/community content where upstream shipped an update after this copy
 * was created — the legacy NEW/UPDATED badge logic, origin-gated.
 */
export function workbookUpdateAvailable(
  w: Pick<Workbook, 'author' | 'create_tstamp' | 'last_updated_tstamp'>,
): boolean {
  if (workbookOrigin(w) === 'custom') return false
  return (
    w.last_updated_tstamp !== null &&
    w.create_tstamp !== null &&
    w.last_updated_tstamp > w.create_tstamp
  )
}

/** Strings out of an unknown-shaped list response: accepts arrays of
 * strings or of objects with a name-ish field, under result/data/root. */
export function readStringList(json: unknown): string[] {
  const body =
    typeof json === 'object' && json !== null
      ? ((json as Record<string, unknown>).result ??
        (json as Record<string, unknown>).data ??
        json)
      : json
  if (!Array.isArray(body)) return []
  return body
    .map((v) => {
      if (typeof v === 'string') return v.trim()
      if (typeof v === 'object' && v !== null) {
        const o = v as Record<string, unknown>
        for (const k of ['name', 'Name', 'subtechnique', 'timezone', 'value']) {
          if (typeof o[k] === 'string') return (o[k] as string).trim()
        }
      }
      return ''
    })
    .filter(Boolean)
}

/**
 * models/get_subtechnique_data, captured 2026-09-29:
 * `data: { TechniqueID, TechniqueName, Subtechniques: [{ SubTechniqueID, SubTechniqueName }] }`.
 * Returns the options in the form a signal stores (`DetectionSubtechnique`,
 * "T1592 - Gather Victim Host Information"): the technique ITSELF first — the
 * default when the technique changes — then each sub-technique.
 */
export function readSubtechniqueOptions(json: unknown): string[] {
  const d = typeof json === 'object' && json !== null ? (json as Record<string, unknown>).data : null
  if (typeof d !== 'object' || d === null) return []
  const o = d as Record<string, unknown>
  const opt = (id: unknown, name: unknown) =>
    typeof id === 'string' && id && typeof name === 'string' && name ? `${id} - ${name}` : null
  const parent = opt(o.TechniqueID, o.TechniqueName)
  const subs = (Array.isArray(o.Subtechniques) ? o.Subtechniques : []).map((x) =>
    typeof x === 'object' && x !== null
      ? opt((x as Record<string, unknown>).SubTechniqueID, (x as Record<string, unknown>).SubTechniqueName)
      : null,
  )
  return [parent, ...subs].filter((x): x is string => x !== null)
}

/* --------------------------------------------------------------------- api */

export interface WorkbooksQuery {
  clusterId: string
  scopeId: string
  pageNo?: number
}

export interface WorkbooksApi {
  /**
   * The full list. Legacy sends addfilters for server-side filtering; the
   * console filters client-side, so this sends none.
   */
  list: (q: WorkbooksQuery) => Promise<Workbook[]>
  /**
   * The same request as `list`, keeping the envelope's `count` — every
   * workbook in scope, not just the rows this default-size page returned.
   */
  listOverview: (q: WorkbooksQuery) => Promise<WorkbookPage>
  /**
   * One server page — pageno/pagesize/addfilters, the total from the
   * envelope's `count`. The paged library spine.
   */
  listPage: (
    q: WorkbooksQuery & { pageNo: number; pageSize: number; filters: WorkbookListFilter[] },
  ) => Promise<WorkbookPage>
  /**
   * The full document for one workbook — the legacy editor loads it via
   * the SAME list endpoint with an id filter; data[0] carries blocks,
   * config.parameters, description. Null when the id matches nothing.
   */
  detail: (q: WorkbooksQuery & { id: string }) => Promise<WorkbookDoc | null>
  /**
   * Version history for one workbook — POST /api/workbook/versionlist.
   * The wire order is chronological; this returns NEWEST FIRST (the legacy
   * panel reverses before rendering).
   */
  versions: (q: WorkbooksQuery & { id: string }) => Promise<WorkbookVersion[]>
  /** One historical version's full document — POST /api/workbook/versionview. */
  /**
   * `ssid`: the legacy client also sends the session id in THIS body (captured
   * 2026-09-29, beside the SSID header) — passed by the caller from its session.
   */
  versionDoc: (q: WorkbooksQuery & { id: string; version: string; ssid?: string }) => Promise<WorkbookDoc | null>
  /** Revert to a version — POST /api/workbook/versionrevert. The version's
   * content becomes a new latest version. */
  revert: (q: WorkbooksQuery & { id: string; version: string }) => Promise<void>
  /** Stage / profile option lists — GET /api/workbook/meta?type= (zod-read). */
  meta: (q: { clusterId: string; type: WorkbookMetaType }) => Promise<string[]>
  /**
   * Save an edited document — POST /api/workbook/edit with
   * {scope_id, wbk_data} (serializeWorkbookSave builds wbk_data).
   */
  /** `ssid`: the legacy edit also sends the session id at the top of the body (captured 2026-09-29). */
  save: (q: WorkbooksQuery & { wbkData: Record<string, unknown>; ssid?: string }) => Promise<void>
  /**
   * Create — POST /api/workbook/add, same envelope minus id. Returns the
   * new id when the response carries one (tolerantly read; unverified).
   */
  create: (q: WorkbooksQuery & { wbkData: Record<string, unknown> }) => Promise<string | null>
  /**
   * Lock/unlock (content-manager concept) — POST /api/workbook/lock_unlock
   * {scope_id, wb_ids, lock}. A locked workbook refuses edits downstream.
   */
  lockUnlock: (q: WorkbooksQuery & { ids: string[]; lock: boolean }) => Promise<void>
  /** Read-only lock check before opening a workbook — POST /api/workbook/checklockedworkbook. */
  checkLocked: (q: WorkbooksQuery & { wbid: string }) => Promise<WorkbookLockCheck>
  /**
   * Generated detection documentation for one workbook —
   * POST /api/models/get_workbook_kb_data. data[0] carries kb_data
   * (inline markdown) and/or kb_file_url. Tolerant read.
   */
  kbData: (
    q: WorkbooksQuery & {
      workbookId: string
      version: string
      signalName: string
      streams: string[]
    },
  ) => Promise<{ kbData: string; kbFileUrl: string } | null>
  /**
   * Request AI documentation generation — the legacy two-step:
   * POST /wrk/api/model_usage_data (consent; returns request_id), then
   * POST /api/models/workbook_documentation_generator. Generation is
   * asynchronous server-side; read back via kbData later.
   */
  kbGenerate: (
    q: WorkbooksQuery & {
      workbookId: string
      version: string
      signalName: string
      subtechnique: string
      queries: string[]
      streams: string[]
      user: { name: string; email: string; role: string; userType: string; licenseType: string }
    },
  ) => Promise<void>
  /** Schedulable timezones — GET /wrk/api/timezones. Tolerant string list. */
  timezones: (q: { clusterId: string }) => Promise<string[]>
  /**
   * MITRE sub-technique options for one technique —
   * POST /api/models/get_subtechnique_data {TechniqueName} (the legacy body,
   * captured 2026-09-29). The technique's own "ID - Name" comes first.
   */
  subtechniques: (q: WorkbooksQuery & { techniqueName: string }) => Promise<string[]>
  /**
   * Enable/disable, bulk-capable — POST /api/workbook/visibility with
   * {scope_id, ids, visibility: 'enable'|'disable'} (legacy
   * disableEnableMultipleWorkbook). Same unverified-capture caveat.
   */
  setVisibility: (
    q: WorkbooksQuery & { ids: string[]; visibility: 'enable' | 'disable' },
  ) => Promise<void>
}

export function createWorkbooksApi(client: ApiClient): WorkbooksApi {
  return {
    list: async ({ clusterId, scopeId, pageNo = 1 }) => {
      const res = await client.request<unknown>('POST', `/${clusterId}/api/workbook/list`, {
        body: { scope_id: scopeId, pageno: pageNo, addfilters: [] },
        rawEnvelope: true,
      })
      return readWorkbooks(res)
    },

    // listOverview / listPage / versionDoc are zod-read and keep the envelope
    // check: their `status` is the plain envelope status (no row carries a
    // `status` key — it is `wb_status`), so an expired session (`invalid`)
    // must reach the app instead of rendering an empty library.
    listOverview: async ({ clusterId, scopeId, pageNo = 1 }) => {
      const res = await client.request('POST', `/${clusterId}/api/workbook/list`, {
        body: { scope_id: scopeId, pageno: pageNo, addfilters: [] },
        schema: workbookListResponse,
      })
      return readWorkbookPage(res)
    },

    listPage: async ({ clusterId, scopeId, pageNo, pageSize, filters }) => {
      const res = await client.request('POST', `/${clusterId}/api/workbook/list`, {
        body: {
          scope_id: scopeId,
          pageno: pageNo,
          pagesize: pageSize,
          addfilters: filters.map(workbookFilterWire),
        },
        schema: workbookListResponse,
      })
      return readWorkbookPage(res)
    },

    detail: async ({ clusterId, scopeId, id }) => {
      const res = await client.request<unknown>('POST', `/${clusterId}/api/workbook/list`, {
        body: {
          scope_id: scopeId,
          pageno: 1,
          addfilters: [
            { filter_key: 'id', filter_condition: 'AND', filter_operand: '=', filter_value: id },
          ],
        },
        rawEnvelope: true,
      })
      const body =
        typeof res === 'object' && res !== null ? (res as Record<string, unknown>).data : null
      return Array.isArray(body) && body.length ? readWorkbookDoc(body[0]) : null
    },

    versions: async ({ clusterId, scopeId, id }) => {
      const res = await client.request<unknown>('POST', `/${clusterId}/api/workbook/versionlist`, {
        body: { scope_id: scopeId, id },
        rawEnvelope: true,
      })
      const body =
        typeof res === 'object' && res !== null ? (res as Record<string, unknown>).data : null
      const rows = Array.isArray(body)
        ? body.map(readWorkbookVersion).filter((v): v is WorkbookVersion => v !== null)
        : []
      return rows.reverse()
    },

    versionDoc: async ({ clusterId, scopeId, id, version, ssid }) => {
      const res = await client.request('POST', `/${clusterId}/api/workbook/versionview`, {
        body: { ...(ssid ? { ssid } : {}), id, scope_id: scopeId, version: versionWire(version) },
        schema: workbookVersionViewResponse,
      })
      const doc = Array.isArray(res.data) ? res.data[0] : res.data
      return doc ? readWorkbookDoc(doc) : null
    },

    revert: async ({ clusterId, scopeId, id, version }) => {
      const res = await client.request('POST', `/${clusterId}/api/workbook/versionrevert`, {
        body: { scope_id: scopeId, id, version: versionWire(version) },
        schema: ackEnvelope,
      })
      assertAck(res, 'Reverting the workbook')
    },

    meta: async ({ clusterId, type }) => {
      const res = await client.request('GET', `/${clusterId}/api/workbook/meta?type=${type}`, {
        schema: workbookMetaResponse,
      })
      return readWorkbookMeta(res)
    },

    save: async ({ clusterId, scopeId, wbkData, ssid }) => {
      // A refused edit is a 200 `{ status: 'failed', message }` (e.g. "Unable
      // to schedule a workbook") — without the ack check it resolves as saved.
      const res = await client.request('POST', `/${clusterId}/api/workbook/edit`, {
        body: { ...(ssid ? { ssid } : {}), scope_id: scopeId, wbk_data: wbkData },
        schema: ackEnvelope,
      })
      assertAck(res, 'Saving the workbook')
    },

    create: async ({ clusterId, scopeId, wbkData }) => {
      const { id: _dropped, ...rest } = wbkData
      const res = await client.request<unknown>('POST', `/${clusterId}/api/workbook/add`, {
        body: { scope_id: scopeId, wbk_data: rest },
        rawEnvelope: true,
      })
      const body =
        typeof res === 'object' && res !== null ? (res as Record<string, unknown>).data : null
      const candidate = Array.isArray(body) ? body[0] : body
      if (typeof candidate === 'string' || typeof candidate === 'number') return String(candidate)
      if (typeof candidate === 'object' && candidate !== null) {
        const id = (candidate as Record<string, unknown>).id
        if (typeof id === 'string' || typeof id === 'number') return String(id)
      }
      return null
    },

    lockUnlock: async ({ clusterId, scopeId, ids, lock }) => {
      await client.request<unknown>('POST', `/${clusterId}/api/workbook/lock_unlock`, {
        body: { scope_id: scopeId, wb_ids: ids, lock },
        rawEnvelope: true,
      })
    },

    checkLocked: async ({ clusterId, scopeId, wbid }) => {
      const res = await client.request('POST', `/${clusterId}/api/workbook/checklockedworkbook`, {
        body: { scope_id: scopeId, wbid },
        schema: workbookLockResponse,
      })
      return readWorkbookLock(res)
    },

    kbData: async ({ clusterId, scopeId, workbookId, version, signalName, streams }) => {
      const res = await client.request<unknown>(
        'POST',
        `/${clusterId}/api/models/get_workbook_kb_data`,
        {
          body: {
            scope_id: scopeId,
            workbook_id: workbookId,
            current_wb_version: version,
            revert_wb_version: '',
            // The legacy caller munges the name this way before asking.
            signal_name: signalName.replaceAll(' ', '_').replace('/', ' '),
            stream: streams,
          },
          rawEnvelope: true,
        },
      )
      const body =
        typeof res === 'object' && res !== null ? (res as Record<string, unknown>).data : null
      const row = Array.isArray(body) ? body[0] : null
      if (typeof row !== 'object' || row === null) return null
      const o = row as Record<string, unknown>
      return {
        kbData: typeof o.kb_data === 'string' ? o.kb_data : '',
        kbFileUrl: typeof o.kb_file_url === 'string' ? o.kb_file_url : '',
      }
    },

    kbGenerate: async ({ clusterId, scopeId, workbookId, version, signalName, subtechnique, queries, streams, user }) => {
      const usage = await client.request<unknown>('POST', `/${clusterId}/wrk/api/model_usage_data`, {
        body: {
          user_name: user.name,
          email_id: user.email,
          user_role: user.role,
          user_type: user.userType,
          license_type: user.licenseType,
          consent: true,
          feature_name: 'Workbook Document Generator',
        },
        rawEnvelope: true,
      })
      const requestId =
        typeof usage === 'object' && usage !== null
          ? (usage as Record<string, unknown>).data
          : null
      await client.request<unknown>(
        'POST',
        `/${clusterId}/api/models/workbook_documentation_generator`,
        {
          body: {
            scope_id: scopeId,
            request_id: typeof requestId === 'string' || typeof requestId === 'number' ? requestId : '',
            signals_name: signalName,
            query: queries,
            workbook_id: workbookId,
            DetectionSubtechnique: subtechnique,
            workbook_version: version,
            stream: streams,
          },
          rawEnvelope: true,
        },
      )
    },

    timezones: async ({ clusterId }) => {
      const res = await client.request<unknown>('GET', `/${clusterId}/wrk/api/timezones`, {
        rawEnvelope: true,
      })
      return readStringList(res)
    },

    subtechniques: async ({ clusterId, techniqueName }) => {
      const res = await client.request<unknown>(
        'POST',
        `/${clusterId}/api/models/get_subtechnique_data`,
        { body: { TechniqueName: techniqueName }, rawEnvelope: true },
      )
      return readSubtechniqueOptions(res)
    },

    setVisibility: async ({ clusterId, scopeId, ids, visibility }) => {
      await client.request<unknown>('POST', `/${clusterId}/api/workbook/visibility`, {
        body: { scope_id: scopeId, ids, visibility },
        rawEnvelope: true,
      })
    },
  }
}
